import {
  Box,
  DatepickerSimple,
  RailzCustomer,
  RailzInventoryItem,
  RailzTaxRate,
  RailzTrackingCategory,
  Select,
  TextArea,
  TextField,
  Tooltip,
} from 'vopay';
import { FieldData } from '../../Form/types';
import * as Yup from 'yup';
import styles from './CreateInvoicePage.module.scss';
import { format } from 'date-fns';
export type LineItem = {
  Description: string;
  Amount: string;
  Quantity: string;
  InventoryRefID: string;
  TaxRateID?: string;
  TrackingCategoryID: string;
  Rate: string;
};
export type InvoiceSubmission = {
  InvoiceNumber: string;
  PostedDate: string;
  DueDate: string;
  CustomerRefID: string;
  CustomerEmail: string;
  CustomerMailingAddress: string;
  Terms: 'Due on Receipt' | 'Custom' | 'Net 15' | 'Net 30' | 'Net 60' | 'Net 80' | 'Net 90' | '';
  Memo: string;
  Lines: LineItem[];
};

export function buildInvoiceNumberRows(): FieldData<InvoiceSubmission>[] {
  return [
    {
      label: () => '',
      fieldName: 'InvoiceNumber',
      getInputState: () => 'required',
      showField: () => true,
      serializeField: (values: InvoiceSubmission) => values.InvoiceNumber,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return (
          <Box maxwidth="128px" ai="fe">
            <TextField maxLength={21} variant="outlineShadow" placeholder="" {...fieldData} />
          </Box>
        );
      },
      validate: () => Yup.mixed().required('This value is required.'),
    },
  ];
}

export function buildInvoiceNoteRows(): FieldData<InvoiceSubmission>[] {
  return [
    {
      label: () => 'Message',
      fieldName: 'Memo',
      getInputState: () => 'optional',
      showField: () => true,
      serializeField: (values: InvoiceSubmission) => values.Memo,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return (
          <TextArea
            maxLength={4000}
            variant="outlinedShadow"
            placeholder="Type your message here"
            height="95px"
            {...fieldData}
          />
        );
      },
      validate: () => Yup.mixed().notRequired(),
    },
  ];
}

export function buildInvoiceFormRows(customers: RailzCustomer[]): FieldData<InvoiceSubmission>[] {
  const termsOptions = [
    { value: 'Due on Receipt', label: 'Due on Receipt' },
    { value: 'Custom', label: 'Custom' },
    { value: 'Net 15', label: 'Net 15' },
    { value: 'Net 30', label: 'Net 30' },
    { value: 'Net 60', label: 'Net 60' },
    { value: 'Net 80', label: 'Net 80' },
    { value: 'Net 90', label: 'Net 90' },
  ];
  return [
    {
      label: () => 'Customer',
      fieldName: 'CustomerRefID',
      getInputState: () => 'required',
      showField: () => true,
      serializeField: (values: InvoiceSubmission) => values.CustomerRefID,
      renderField: ({ ctx, getFormikData }) => {
        const fieldData = getFormikData<string>({
          onChange: (_, args) => {
            const data: RailzCustomer = args.data;
            if (!data) return;
            ctx.setFieldValue('CustomerEmail', data.emailAddress || '');
            ctx.setFieldValue(
              'CustomerMailingAddress',
              [
                data.shippingAddress?.line1,
                data.shippingAddress?.city,
                data.shippingAddress?.region,
                data.shippingAddress?.postalCode,
                data.shippingAddress?.country,
              ]
                .filter(Boolean)
                .join(', '),
            );
          },
        });
        return (
          <Select
            version="2"
            options={customers}
            optionValue={(customer) => customer.id}
            optionLabel={(customer) => customer.contactName}
            {...fieldData}
          />
        );
      },
      validate: () => Yup.mixed().required('This value is required.'),
    },
    {
      label: () => 'Customer Email',
      fieldName: 'CustomerEmail',
      getInputState: () => 'required',
      showField: () => true,
      serializeField: (values: InvoiceSubmission) => values.CustomerEmail,

      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return <TextField variant="outlineShadow" readOnly placeholder="" {...fieldData} />;
      },
      validate: () => Yup.mixed().required('This value is required.'),
    },
    {
      label: () => 'Mailing Address',
      fieldName: 'CustomerMailingAddress',

      getInputState: () => 'required',
      showField: () => true,
      serializeField: (values: InvoiceSubmission) => values.CustomerMailingAddress,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return <TextField variant="outlineShadow" readOnly placeholder="" {...fieldData} />;
      },
      validate: () => Yup.mixed().required('This value is required.'),
    },
    {
      label: () => 'Terms',
      fieldName: 'Terms',
      tooltip: () => (
        <Tooltip
          information="Terms indicate the number of days a customer has to pay you. You can adjust this before sending the invoice.
"
          position="top"
          width="394px"
        />
      ),
      getInputState: () => 'required',
      showField: () => true,
      serializeField: (values: InvoiceSubmission) => values.Terms,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return (
          <Select
            version="2"
            options={termsOptions}
            optionValue={(customer) => customer.value}
            optionLabel={(customer) => customer.label}
            {...fieldData}
          />
        );
      },
      validate: () => Yup.mixed().required('This value is required.'),
    },
    {
      label: () => 'Posted Date',
      fieldName: 'PostedDate',
      getInputState: () => 'required',
      showField: () => true,
      serializeField: (values) => {
        const date = new Date(Number(values.PostedDate));
        return format(date, 'yyyy-MM-dd');
      },

      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return <DatepickerSimple variant="outlineShadow" placeholder="" {...fieldData} />;
      },
      validate: () => Yup.mixed().required('This value is required.'),
    },
    {
      label: () => 'Due Date',
      fieldName: 'DueDate',
      getInputState: () => 'required',
      showField: () => true,
      serializeField: (values) => {
        const date = new Date(Number(values.DueDate));
        return format(date, 'yyyy-MM-dd');
      },
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return <DatepickerSimple variant="outlineShadow" placeholder="" {...fieldData} />;
      },
      validate: () => Yup.mixed().required('This value is required.'),
    },
  ];
}

export function buildTableFormRows(
  inventory: RailzInventoryItem[],
  salesTaxes: RailzTaxRate[],
  trackingCategories: RailzTrackingCategory[],
  currency: string,
  taxCalculation: 'inclusive' | 'exclusive' | 'outOfScope',
): FieldData<LineItem>[] {
  return [
    {
      label: () => '',
      fieldName: 'InventoryRefID',
      getInputState: () => 'required',
      showField: () => true,
      serializeField: (values) => values.InventoryRefID,
      renderField: ({ ctx, getFormikData }) => {
        const fieldData = getFormikData<string>({
          onChange: (_, args) => {
            const data: RailzInventoryItem | undefined = args.data;
            if (!data) return;
            ctx.setFieldValue('Rate', data.invoiceItem?.unitPrice.toString());
          },
        });
        return (
          <Select
            version="2"
            options={inventory}
            optionValue={(customer) => customer.id}
            optionLabel={(customer) => customer.name}
            {...fieldData}
          />
        );
      },
      validate: () => Yup.mixed().required('This value is required.'),
    },
    {
      label: () => '',
      fieldName: 'Description',
      getInputState: () => 'required',
      showField: () => true,
      serializeField: (values) => values.Description,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return <TextField variant="outlineShadow" placeholder="" {...fieldData} />;
      },
      validate: () => Yup.mixed().required('This value is required.'),
    },
    {
      label: () => '',
      fieldName: 'Quantity',
      getInputState: () => 'required',
      showField: () => true,
      serializeField: (values) => values.Amount,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return (
          <TextField
            variant="outlineShadow"
            preset="numericWholePositive"
            inputClass={styles.quantityField}
            placeholder=""
            {...fieldData}
          />
        );
      },
      validate: () => Yup.mixed().required('This value is required.'),
    },
    {
      label: () => '',
      fieldName: 'Rate',
      getInputState: () => 'required',
      showField: () => true,
      serializeField: (values) => values.Rate,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return (
          <TextField
            elementLeft={<div className={styles.currency}>{currency}</div>}
            variant="outlineShadow"
            preset="currency"
            placeholder=""
            {...fieldData}
          />
        );
      },
      validate: () => Yup.mixed().required('This value is required.'),
    },

    {
      label: () => '',
      fieldName: 'Amount',
      getInputState: () => 'required',
      showField: () => true,
      serializeField: (values) => values.Amount,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return (
          <TextField
            variant="outlineShadow"
            preset="currency"
            placeholder=""
            elementLeft={<div className={styles.currency}>{currency}</div>}
            {...fieldData}
          />
        );
      },
      validate: () => Yup.mixed().required('This value is required.'),
    },

    {
      label: () => '',
      fieldName: 'TaxRateID',
      getInputState: () => 'required',
      showField: () => true,
      serializeField: (values) => values.TaxRateID,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return (
          <Select
            version="2"
            options={salesTaxes}
            optionValue={(tax) => tax.id}
            optionLabel={(tax) => tax.name}
            {...fieldData}
          />
        );
      },
      validate: () => (taxCalculation == 'outOfScope' ? undefined : Yup.mixed().required('This value is required.')),
    },
    {
      label: () => '',
      fieldName: 'TrackingCategoryID',
      getInputState: () => 'required',
      showField: () => true,
      serializeField: (values) => values.TrackingCategoryID,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return (
          <Select
            version="2"
            options={trackingCategories}
            optionValue={(category) => category.id}
            optionLabel={(category) => category.name}
            {...fieldData}
          />
        );
      },
      validate: () => Yup.mixed().required('This value is required.'),
    },
  ];
}
